import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { type GridRowsProp, type GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';

import CategoryDelete from './CategoryDelete.js';
import CategoryForm from './CategoryForm.js';
import { useAllCategories } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type CodeCategory } from '../../models/index.js';
import { humanize } from '../../utils/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

const CategoriesTable = () => {
  const { data = [], isLoading, refetch } = useAllCategories();

  const {open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen} = useDialogControls<CodeCategory>(data);
  const {open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete} = useDialogControls<CodeCategory>(data);

  const { canCreateCode, canUpdateCode, canDeleteCode } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'name', headerName: 'Name', flex: 1 },
      {
        field: 'color',
        headerName: 'Color',
        width: 150,
        headerAlign: 'center',
        renderCell: (params) => <Box sx={{ backgroundColor: params.value, height: 'calc(100% - 16px)', m: 1 }} />,
      },
      { field: 'showInHeaderAs', headerName: 'Show In Header As', width: 200 },
      { field: 'showInReport', headerName: 'Show In Report', width: 200, type: 'boolean' },
    ];
    if (canUpdateCode || canDeleteCode) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canUpdateCode,
          canDelete: canDeleteCode,
        }),
      });
    }
    return cols;
  }, [canUpdateCode, canDeleteCode, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        name: item.name,
        color: item.color,
        showInHeaderAs: humanize(item.showInHeaderAs),
        showInReport: item.showInReport,
      })),
    [data],
  );

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Stack direction={'row'} justifyContent={'space-between'} sx={{ my: 1 }}>
        <h2 style={{ margin: 0 }}>Categories</h2>
        {canCreateCode ? (
          <Tooltip arrow color="primary" placement="bottom" title={'Add a new category'}>
            <IconButton color="inherit" aria-label="Add a new category" onClick={() => setFormOpen(true)} >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Table
        columns={columns}
        rows={rows}
        NoRowsOverlayText={'No categories available.'}
        isLoading={isLoading}
      />
      <CategoryForm
        open={formOpen}
        onClose={resetForm}
        category={formData}
        refetch={refetch}
      />
      <CategoryDelete
        open={deleteOpen}
        onClose={resetDelete}
        category={deleteData}
        refetch={refetch}
      />
    </Box>
  );
};

export default CategoriesTable;
